import { Button, Text } from "north.js";
import React, { useMemo, useState } from "react";
import styled from "styled-components";

import { Header, PageWrapper, Spinner } from "src/shared/components";
import { errorToast, successToast } from "src/shared/components/Toast";
import {
  ProfileContextProvider,
  useProfileContext,
} from "src/shared/contexts/ProfileContext";
import { HackerAPIFile } from "src/shared/utils/hackerapi";
import { DiscoveryProfile } from "src/views/hacker-directory/shared/types";
import {
  ProfileFormValues,
  TargetStage,
} from "src/views/profile/components/ProfileForm/types";
import {
  buildDiscoveryProfileMutationValues,
  buildProfileFormValues,
} from "src/views/profile/components/ProfileForm/values";

import EditProfile from "./components/EditProfile";
import EmptyProfile from "./components/EmptyProfile";
import Profile from "./components/Profile";

const YourProfileController: React.FC = () => {
  const [isEditing, setIsEditing] = useState(false);
  const {
    profile,
    isLoading,
    loadError,
    isUpdating,
    upsertDiscoveryProfile,
    refetch,
  } = useProfileContext();
  const formValues = useMemo(() => buildProfileFormValues(profile), [profile]);
  const displayProfile = useMemo<DiscoveryProfile | null>(() => {
    if (!profile) return null;

    const photo = Array.isArray(profile.profile_photo)
      ? profile.profile_photo[0]
      : profile.profile_photo;

    return {
      ...profile,
      id: profile.claimId,
      profilePhotoCaption: profile.profile_photo_caption,
      bioPrompt: profile.bio_prompt,
      experienceGoals: profile.experience_goals,
      weekendPace: profile.weekend_pace,
      teammatesNeeded: profile.teammates_needed,
      teammateCriteria: profile.teammate_criteria,
      teammatesClaimIds: profile.teammates_claim_ids,
      contactEmail: profile.contact_email,
      contactDiscord: profile.contact_discord,
      contactLinkedin: profile.contact_linkedin,
      contactX: profile.contact_x,
      contactPhone: profile.contact_phone,
      profilePhoto:
        photo != null && typeof photo === "object"
          ? (photo as HackerAPIFile)
          : null,
    };
  }, [profile]);

  const handleSubmit = async (
    values: ProfileFormValues,
    targetStage: TargetStage
  ) => {
    try {
      await upsertDiscoveryProfile({
        ...buildDiscoveryProfileMutationValues(values),
        stage_slug: targetStage,
      });
      successToast(
        targetStage === "published"
          ? "Profile published successfully!"
          : "Profile saved as draft."
      );
      setIsEditing(false);
    } catch (error) {
      errorToast(
        error instanceof Error ? error.message : "Something went wrong."
      );
    }
  };

  if (isLoading) {
    return (
      <>
        <ProfileHeader />
        <ProfilePageWrapper stretchHeight={false}>
          <Spinner text="Loading your profile..." />
        </ProfilePageWrapper>
      </>
    );
  }

  if (isEditing) {
    return (
      <EditProfile
        values={formValues}
        isPublished={profile?.isPublished ?? false}
        isSubmitting={isUpdating}
        onSubmit={handleSubmit}
        onCancel={() => setIsEditing(false)}
      />
    );
  }

  if (!profile || !displayProfile) {
    if (!loadError) {
      return <EmptyProfile onCreate={() => setIsEditing(true)} />;
    }

    return (
      <>
        <ProfileHeader />
        <ProfilePageWrapper stretchHeight={false}>
          <ProfileState role="alert">
            <Text type="h3">Profile unavailable</Text>
            <SecondaryText type="body1">
              We couldn&rsquo;t load your directory profile. Please try again.
            </SecondaryText>
            <Button color="primary" size="md" onClick={() => void refetch()}>
              Try again
            </Button>
          </ProfileState>
        </ProfilePageWrapper>
      </>
    );
  }

  return (
    <Profile
      profile={displayProfile}
      isPublished={profile.isPublished}
      onEdit={() => setIsEditing(true)}
    />
  );
};

const HackerDirectoryYourProfile: React.FC = () => (
  <ProfileContextProvider>
    <YourProfileController />
  </ProfileContextProvider>
);

const ProfileHeader: React.FC = () => (
  <Header
    title="My Profile"
    subtitle="Edit your directory card details, update your project goals, and manage your team status."
  />
);

const ProfilePageWrapper = styled(PageWrapper)`
  padding-bottom: 64px;
`;

const ProfileState = styled.div`
  display: flex;
  min-height: 360px;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  text-align: center;
`;

const SecondaryText = styled(Text)`
  color: ${({ theme }) => theme.globalConstants.color.textSecondary};
`;

export default HackerDirectoryYourProfile;
